import { Storage } from "@lib/storage";
import { WaveformData } from "./types";
import { posthog, AnalyticsEvent } from "@src/analytics";

const AUDIO_CACHE_VERSION = "v3";
const WAVEFORM_CACHE_VERSION = "v3";

// Previous versions (for migration)
const AUDIO_CACHE_VERSION_V2 = "v2";
const WAVEFORM_CACHE_VERSION_V2 = "v2";

export class AudioStorage {
  private storage: Storage;
  private songId: string;
  private downloadKey: (key: string) => Promise<Blob>;

  constructor(songId: string, downloadKey: (key: string) => Promise<Blob>) {
    this.songId = songId;
    this.storage = new Storage();
    this.downloadKey = downloadKey;
  }

  async getAudio(regionId: string, storageKey: string): Promise<Blob> {
    const cacheKey = this.getAudioCacheKey(regionId);
    const deprecatedKey = this.getDeprecatedAudioKey(regionId);
    let audio = await this.storage.getItem<Blob>(cacheKey, deprecatedKey);
    if (audio) {
      posthog.capture(AnalyticsEvent.SONG_REGION_LOADED, { regionId: regionId, fromCache: true });
      return audio;
    }

    posthog.capture(AnalyticsEvent.SONG_REGION_LOADED, { regionId: regionId, fromCache: false });

    audio = await this.downloadKey(storageKey);
    await this.storage.setItem(cacheKey, audio);
    return audio;
  }

  async getWaveform(
    regionId: string,
    waveformKey: string | null,
    calculateWaveform: () => WaveformData,
  ): Promise<WaveformData> {
    const cacheKey = this.getWaveformCacheKey(regionId);
    const deprecatedKey = this.getDeprecatedWaveformKey(regionId);
    let waveform = await this.storage.getItem<WaveformData>(cacheKey, deprecatedKey);
    if (waveform) {
      return waveform;
    }

    if (waveformKey) {
      const blob = await this.downloadKey(waveformKey);
      const text = await blob.text();
      waveform = JSON.parse(text);
    } else {
      posthog.capture(AnalyticsEvent.SONG_WAVEFORM_GENERATED, {
        reason: "missing_waveform_key",
        regionId,
        songId: this.songId,
      });
      waveform = calculateWaveform();
    }

    await this.storage.setItem(cacheKey, waveform);
    return waveform!;
  }

  async setWaveform(regionId: string, waveform: WaveformData): Promise<void> {
    const key = this.getWaveformCacheKey(regionId);
    await this.storage.setItem(key, waveform);
  }

  // New key format: <songId>/audio-v3-<regionId>
  private getAudioCacheKey(regionId: string): string {
    return `${this.songId}/audio-${AUDIO_CACHE_VERSION}-${regionId}`;
  }

  // New key format: <songId>/waveform-v3-<regionId>
  private getWaveformCacheKey(regionId: string): string {
    return `${this.songId}/waveform-${WAVEFORM_CACHE_VERSION}-${regionId}`;
  }

  // Old key format (for migration): audio-v2-<regionId>
  private getDeprecatedAudioKey(regionId: string): string {
    return `audio-${AUDIO_CACHE_VERSION_V2}-${regionId}`;
  }

  // Old key format (for migration): waveform-v2-<regionId>
  private getDeprecatedWaveformKey(regionId: string): string {
    return `waveform-${WAVEFORM_CACHE_VERSION_V2}-${regionId}`;
  }
}
